import { useEffect, useRef } from "react";
import { Box, Container, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import { PublicRestaurantInfo } from "src/data/clientBooking/queries/clientBooking";
import {
  colorPublicMenuAccent,
  colorPublicMenuBg,
  colorPublicMenuHeroScrim,
  colorPublicMenuText,
  colorPublicMenuTextMuted,
} from "src/design/tokens";

interface CoverHeroProps {
  restaurant: PublicRestaurantInfo;
}

const HERO_HEIGHT_VH = 68;

// Full-bleed cover image that fades out as the visitor scrolls. The hero sits in
// normal flow at the top; a rAF-throttled scroll listener maps scrollY over the
// hero height to an opacity written straight to the node (no React re-render).
export const CoverHero = ({ restaurant }: CoverHeroProps) => {
  const mediaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const node = mediaRef.current;
        if (!node) return;
        const fadeOver = node.offsetHeight || 1;
        const opacity = Math.max(0, 1 - window.scrollY / fadeOver);
        node.style.opacity = String(opacity);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const metaItems = [
    { icon: <LocationOnIcon sx={{ fontSize: 16 }} />, value: restaurant.address },
    restaurant.openingDisplay
      ? {
          icon: <AccessTimeIcon sx={{ fontSize: 16 }} />,
          value: restaurant.openingDisplay,
        }
      : null,
    { icon: <PhoneIcon sx={{ fontSize: 16 }} />, value: restaurant.phone },
  ].filter((m): m is { icon: JSX.Element; value: string } => Boolean(m));

  return (
    <Box sx={{ position: "relative" }}>
      {/* Cover media + scrim — this is the layer that fades on scroll. */}
      <Box
        ref={mediaRef}
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          willChange: "opacity",
        }}
      >
        {restaurant.coverUrl ? (
          <Box
            component="img"
            src={restaurant.coverUrl}
            alt={restaurant.name}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Box sx={{ width: "100%", height: "100%", bgcolor: colorPublicMenuBg }} />
        )}
        {/* Scrim: darken for legibility and fade into the page background. */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(180deg, ${colorPublicMenuHeroScrim} 0%, ${colorPublicMenuHeroScrim} 45%, ${colorPublicMenuBg} 100%)`,
          }}
        />
      </Box>

      {/* Identity block — stays crisp above the fading media. */}
      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          minHeight: `${HERO_HEIGHT_VH}vh`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          pb: { xs: 4, md: 6 },
          pt: { xs: 8, md: 10 },
        }}
      >
        {restaurant.description && (
          <Typography
            sx={{
              color: colorPublicMenuAccent,
              textTransform: "uppercase",
              letterSpacing: "0.22em",
              fontSize: { xs: 11, md: 12 },
              fontWeight: 600,
              mb: { xs: 1, md: 1.5 },
            }}
          >
            {restaurant.description}
          </Typography>
        )}

        <Typography
          sx={{
            fontFamily: "Bogart-bold",
            color: colorPublicMenuText,
            fontSize: { xs: 44, md: 72 },
            lineHeight: 1.02,
            mb: { xs: 2, md: 2.5 },
          }}
        >
          {restaurant.name}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            columnGap: { xs: 2, md: 3 },
            rowGap: 1,
          }}
        >
          {metaItems.map((item) => (
            <Box
              key={item.value}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                color: colorPublicMenuTextMuted,
              }}
            >
              {item.icon}
              <Typography sx={{ fontSize: { xs: 13, md: 14 } }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};
